class CartDrawer extends HTMLElement {
	constructor() {
		super();

		this.addEventListener(
			"keyup",
			(evt) => evt.code === "Escape" && this.close()
		);
		this.addEventListener("click", (evt) => {
			if (evt.target && evt.target.matches("#CartDrawer-Overlay, .cart-drawer__overlay")) {
				this.close();
			}
		});
		const overlay = this.querySelector("#CartDrawer-Overlay");
		if (overlay) {
			overlay.addEventListener("click", this.close.bind(this));
		}
		this.setHeaderCartIconAccessibility();
	}

	setHeaderCartIconAccessibility() {
		const cartLink = document.querySelector("#cart-icon-bubble");
		if (!cartLink) return;
		cartLink.setAttribute("role", "button");
		cartLink.setAttribute("aria-haspopup", "dialog");

		cartLink.addEventListener("click", (event) => {
			event.preventDefault();

			const bubbleMatch = cartLink.innerText.match(/\d+/);
			const bubbleCount = bubbleMatch ? parseInt(bubbleMatch[0], 10) : 0;
			const isDrawerEmpty = this.classList.contains("is-empty") || !!this.querySelector(".drawer__inner-empty");

			// If the header shows items but the drawer is in an empty state, fetch latest drawer before opening
			if (bubbleCount > 0 && isDrawerEmpty) {
				const cartUrl = (window.routes && window.routes.cart_url) || "/cart";
				fetch(`${cartUrl}?sections=cart-drawer,cart-icon-bubble`, {
					headers: { "X-Requested-With": "XMLHttpRequest" },
					cache: "no-store",
				})
					.then((r) => r.json())
					.then((sections) => {
						this.updateDrawerSilently(sections);
					})
					.catch(() => {});
			}

			this.open(cartLink);
		});

		cartLink.addEventListener("keydown", (event) => {
			if (event.code.toUpperCase() === "SPACE") {
				event.preventDefault();
				this.open(cartLink);
			}
		});
	}

	open(triggeredBy) {
		if (triggeredBy) this.setActiveElement(triggeredBy);
		const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
		if (cartDrawerNote && !cartDrawerNote.hasAttribute("role"))
			this.setSummaryAccessibility(cartDrawerNote);
		// here the animation doesn't seem to always get triggered. A timeout seem to help
		setTimeout(() => {
			this.classList.add("animate", "active");
		});

		this.addEventListener(
			"transitionend",
			() => {
				const containerToTrapFocusOn = this.classList.contains("is-empty")
					? this.querySelector(".drawer__inner-empty")
					: document.getElementById("CartDrawer");
				const focusElement =
					this.querySelector(".drawer__inner") ||
					this.querySelector(".drawer__close");
				trapFocus(containerToTrapFocusOn, focusElement);
			},
			{ once: true }
		);

		document.body.classList.add("cart-overflow-hidden");
		document.body.classList.add("overflow-hidden");
	}

	close() {
		this.classList.remove("active");
		removeTrapFocus(this.activeElement);
		document.body.classList.remove("cart-overflow-hidden");
		document.body.classList.remove("overflow-hidden");

		setTimeout(() => {
			let notification = this.querySelector(".notification");

			if (notification) {
				const activeItem = this.querySelector(
					`.cart-item[data-product-id="${this.productId}"`
				);
				notification.classList.remove("notification-open");
				activeItem?.classList.remove("active");
			}
		}, 1000);
	}

	setSummaryAccessibility(cartDrawerNote) {
		cartDrawerNote.setAttribute("role", "button");
		cartDrawerNote.setAttribute("aria-expanded", "false");

		if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
			cartDrawerNote.setAttribute(
				"aria-controls",
				cartDrawerNote.nextElementSibling.id
			);
		}

		cartDrawerNote.addEventListener("click", (event) => {
			event.currentTarget.setAttribute(
				"aria-expanded",
				!event.currentTarget.closest("details").hasAttribute("open")
			);
		});

		cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
	}

	renderContents(parsedState) {
		this.productId = parsedState.id;
		this.getSectionsToRender().forEach((section) => {
			const sectionElement = section.selector
				? document.querySelector(section.selector)
				: document.getElementById(section.id);
			if (sectionElement && parsedState.sections && parsedState.sections[section.id]) {
				sectionElement.innerHTML = this.getSectionInnerHTML(
					parsedState.sections[section.id],
					section.selector
				);
			}
		});

		// Manage is-empty on <cart-drawer>
		if (parsedState.sections && parsedState.sections["cart-drawer"]) {
			const isDrawerEmpty = parsedState.sections["cart-drawer"].includes("drawer__inner-empty");
			if (isDrawerEmpty) {
				this.classList.add("is-empty");
			} else {
				this.classList.remove("is-empty");
			}
		} else {
			this.classList.remove("is-empty");
		}

		try {
			const cartIconBubble = document.getElementById("cart-icon-bubble");
			if (cartIconBubble) {
				const match = cartIconBubble.innerText.match(/\d+/);
				if (match) {
					localStorage.setItem("cart_count", match[0]);
					localStorage.setItem("cart_timestamp", Date.now().toString());
				}
			}
		} catch (e) {}

		let notification = this.querySelector(".notification");

		if (notification) {
			const activeItem = this.querySelector(
				`.cart-item[data-product-id="${this.productId}"`
			);
			notification.classList.add("notification-open");
			activeItem?.classList.add("active");
		}

		setTimeout(() => {
			this.querySelector("#CartDrawer-Overlay")?.addEventListener(
				"click",
				this.close.bind(this)
			);
			const shouldAutoOpen = this.dataset.autoOpen === "true" || (window.theme && window.theme.autoOpenCartDrawer === true);
			if (shouldAutoOpen) {
				this.open();
			}
		});
	}

	updateDrawerSilently(parsedSections) {
		if (!parsedSections) return;

		if (parsedSections["cart-drawer"]) {
			const targetDrawer = document.getElementById("CartDrawer");
			if (targetDrawer) {
				const parsed = this.getSectionInnerHTML(
					parsedSections["cart-drawer"],
					"#CartDrawer"
				);
				if (parsed) targetDrawer.innerHTML = parsed;
			}

			const isDrawerEmpty = parsedSections["cart-drawer"].includes("drawer__inner-empty");
			if (isDrawerEmpty) {
				this.classList.add("is-empty");
			} else {
				this.classList.remove("is-empty");
			}
		}

		if (parsedSections["cart-icon-bubble"]) {
			const cartBubble = document.getElementById("cart-icon-bubble");
			if (cartBubble) {
				const parsed = this.getSectionInnerHTML(
					parsedSections["cart-icon-bubble"],
					".shopify-section"
				);
				if (parsed) cartBubble.innerHTML = parsed;

				try {
					const match = cartBubble.innerText.match(/\d+/);
					if (match) {
						localStorage.setItem("cart_count", match[0]);
						localStorage.setItem("cart_timestamp", Date.now().toString());
					}
				} catch (e) {}
			}
		}

		const overlay = this.querySelector("#CartDrawer-Overlay");
		if (overlay) {
			overlay.addEventListener("click", this.close.bind(this));
		}
	}

	getSectionInnerHTML(html, selector = ".shopify-section") {
		const doc = new DOMParser().parseFromString(html, "text/html");
		const target = doc.querySelector(selector);
		return target ? target.innerHTML : "";
	}

	getSectionsToRender() {
		return [
			{
				id: "cart-drawer",
				selector: "#CartDrawer",
			},
			{
				id: "cart-icon-bubble",
			},
		];
	}

	getSectionDOM(html, selector = ".shopify-section") {
		return new DOMParser()
			.parseFromString(html, "text/html")
			.querySelector(selector);
	}

	setActiveElement(element) {
		this.activeElement = element;
	}
}

customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
	onCartUpdate() {
		// Handled via CartDrawer and silent sync; avoid redundant /cart?section_id=main-cart-items
		return;
	}

	getSectionsToRender() {
		return [
			{
				id: "CartDrawer",
				section: "cart-drawer",
				selector: ".drawer__inner",
			},
			{
				id: "cart-icon-bubble",
				section: "cart-icon-bubble",
				selector: ".shopify-section",
			},
		];
	}
}

customElements.define("cart-drawer-items", CartDrawerItems);

if (!customElements.get("cart-note")) {
	customElements.define(
		"cart-note",
		class CartNote extends HTMLElement {
			constructor() {
				super();

				this.addEventListener(
					"input",
					debounce((event) => {
						const body = JSON.stringify({ note: event.target.value });
						fetch(`${routes.cart_update_url}`, {
							...fetchConfig(),
							...{ body },
						});
					}, ON_CHANGE_DEBOUNCE_TIMER)
				);
			}
		}
	);
}
